<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppTag from '@/components/atoms/tag/AppTag.vue'

type TagSeverity = 'secondary' | 'success' | 'info' | 'warn' | 'warning' | 'danger' | 'contrast'

const props = defineProps<{
  status: string
  colorMap?: Record<string, TagSeverity>
  labelMap?: Record<string, string>
}>()

const { t, te } = useI18n()

const normalizedStatus = computed(() => props.status.toLowerCase())

const translationKey = computed(() => {
  const status = normalizedStatus.value

  if (te(`general.${status}`)) return `general.${status}`
  if (te(`campaign.status.${status}`)) return `campaign.status.${status}`
  if (te(`contact.status.${status}`)) return `contact.status.${status}`
  if (te(`reports.detailed_calls.${status}`)) return `reports.detailed_calls.${status}`
  if (te(`monitor.asterisk_status.${status}`)) return `monitor.asterisk_status.${status}`

  return null
})

const tagLabel = computed(() => {
  const status = normalizedStatus.value

  if (props.labelMap && Object.prototype.hasOwnProperty.call(props.labelMap, status)) {
    return props.labelMap[status]
  }

  return translationKey.value ? t(translationKey.value) : props.status
})

const tagSeverity = computed((): TagSeverity => {
  const status = normalizedStatus.value

  if (props.colorMap && Object.prototype.hasOwnProperty.call(props.colorMap, status)) {
    return props.colorMap[status]
  }

  if (status === 'active') return 'success'
  if (status === 'inactive') return 'danger'
  if (['ready', 'pending', 'queued', 'waiting', 'paused', 'finished'].includes(status)) {
    return 'warn'
  }
  if (['running', 'connected', 'placed', 'dialing', 'success', 'connect'].includes(status)) {
    return 'success'
  }
  if (['abandoned', 'noanswer', 'noanswer_amd', 'failed', 'deleted', 'abandon'].includes(status)) {
    return 'danger'
  }

  return 'secondary'
})
</script>

<template>
  <AppTag :label="tagLabel" :severity="tagSeverity" />
</template>
